import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import axios from 'axios';
import { FORM_ERROR } from 'final-form';
import arrayMutators from 'final-form-arrays';
import React, { ReactNode } from 'react';
import { Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { AnyObjectSchema } from 'yup';

import { TaxIdKey, TaxIdKeys } from 'apis/financial-data-registry/types';
import { Typography as T } from 'common/typography';
import validateYupSchema from 'common/utils/validateYupSchema';

import { SubmissionErrors } from './SubmissionErrors';
import { taxIdKeyToLabelMap } from './useTaxInfoVerification';
import {
  TaxInfoFormatValidationError,
  isKybSubmissionInvalidStateError,
  isKybSubmissionValidationError,
  isTaxInfoValidationError,
} from '../errors';
import SectionsLayout from '../sections/SectionsLayout';
import { useKybNavigation } from '../useKybNavigation';

type Props<FormValues> = {
  subtitle: string;
  initialFormValues: FormValues;
  schema: AnyObjectSchema;
  feedback?: string;
  children: ({ submitting, values }: { submitting: boolean; values: FormValues }) => ReactNode;
  onSubmit: (data: FormValues) => Promise<void>;
};

const Content = styled.div`
  text-align: start;
  color: ${cbSemanticCssVars.textPrimary};
`;

export const StyledForm = styled.form`
  max-width: 100%;
`;

const StyledFeedback = styled(Alert).attrs({ variant: 'info' })`
  margin-block-start: 16px;
`;

export const ActionsContainer = styled.div`
  display: flex;
  align-items: stretch;
  flex-direction: column;
  margin-block-start: 16px;
  gap: 16px;
  width: 100%;
`;

type GenericFormValues = {
  nextSection: string | undefined;
};

const taxIdKeyToFieldName: Record<TaxIdKey, string> = {
  vatId: 'vatId',
  tin: 'tin',
  additionalTin: 'additionalTins',
};

const buildFieldMessageForTaxIdKey = (taxIdKey: TaxIdKey, message: string) => {
  if (taxIdKey === 'additionalTin') {
    return {
      [taxIdKeyToFieldName[taxIdKey]]: [
        {
          number: message,
        },
      ],
    };
  }

  return {
    [taxIdKeyToFieldName[taxIdKey]]: message,
  };
};

export const SectionForm = <FormValues extends GenericFormValues>({
  subtitle,
  initialFormValues,
  schema,
  feedback,
  children,
  onSubmit,
}: Props<FormValues>) => {
  const { navigate } = useKybNavigation();
  const { formatMessage: t } = useIntl();

  const buildTaxInfoFormatValidationErrorMessage = (invalidTaxIdKeys: TaxIdKeys) => {
    return {
      [FORM_ERROR]: {
        message: t({
          id: 'compliance.kyb.sections.basic-info.business-information.tax-verification.format-validation-error',
        }),
      },
      ...invalidTaxIdKeys.reduce(
        (acc, taxIdKey) => ({
          ...acc,
          ...buildFieldMessageForTaxIdKey(
            taxIdKey,
            t(
              {
                id: 'compliance.kyb.sections.basic-info.business-information.tax-verification.format-validation-field-error',
              },
              {
                field: taxIdKeyToLabelMap[taxIdKey],
              },
            ),
          ),
        }),
        {},
      ),
    };
  };

  const handleSubmit = async (data: FormValues) => {
    try {
      await onSubmit(data);
    } catch (error) {
      // Format validation errors found in the verification result, polled from FDR
      // This happens if tax information had already been sent asynchronously to FDR, so the verification result is already stored in the service
      if (error instanceof TaxInfoFormatValidationError) {
        return buildTaxInfoFormatValidationErrorMessage(error.invalidValues);
      }

      if (!axios.isAxiosError(error)) {
        console.error(error);
        return;
      }

      if (isKybSubmissionValidationError(error)) {
        return {
          [FORM_ERROR]: {
            validationErrors: error.response.data.validationErrors,
            message: error.message,
          },
        };
      }

      if (isKybSubmissionInvalidStateError(error)) {
        navigate('/information-submitted');

        return {
          [FORM_ERROR]: {
            message: error.response.data.message,
          },
        };
      }

      // Format validation errors returned synchronously by FDR (via legitimizer) when trying to update tax information
      if (isTaxInfoValidationError(error)) {
        return buildTaxInfoFormatValidationErrorMessage(error.response.data.taxIdKeys);
      }

      return { [FORM_ERROR]: { message: t({ id: 'errors.unknown-error-retry-contact' }) } };
    }

    return;
  };

  return (
    <Content>
      <Form<FormValues>
        onSubmit={handleSubmit}
        initialValues={initialFormValues}
        mutators={{ ...arrayMutators }}
        validate={validateYupSchema(schema)}
      >
        {({ handleSubmit, submitting, submitError, submitFailed, hasValidationErrors, form, values }) => (
          <SectionsLayout
            onStepChange={nextSection => {
              form.change('nextSection', nextSection);
              handleSubmit();
            }}
          >
            <StyledForm onSubmit={handleSubmit}>
              <T.Body2>{subtitle}</T.Body2>
              {feedback && <StyledFeedback>{feedback}</StyledFeedback>}

              {children({ submitting, values })}

              <ActionsContainer>
                {submitFailed && hasValidationErrors && (
                  <Alert variant="error">
                    <FormattedMessage id="compliance.kyb.errors.validation" />
                  </Alert>
                )}

                {submitError && <SubmissionErrors {...submitError} />}

                <Button size="large" variant="primary" loading={submitting} disabled={submitting} type="submit">
                  <FormattedMessage id="state.continue" />
                </Button>
              </ActionsContainer>
            </StyledForm>
          </SectionsLayout>
        )}
      </Form>
    </Content>
  );
};
